'use client';
import React, { useRef, useEffect, useState } from 'react';

interface Testimonial {
  name: string;
  location: string;
  service: string;
  text: string;
  rating: number;
  initial: string;
  color: string;
}

const testimonials: Testimonial[] = [
{
  name: 'Priya Sharma',
  location: 'Mumbai, India',
  service: 'Quantum Karmic Healing',
  text: 'My session with Shruti was absolutely life-changing. The Quantum Karmic Healing revealed patterns I had been carrying for lifetimes. I finally understand my soul\'s purpose and feel a deep sense of peace I\'ve never experienced before.',
  rating: 5,
  initial: 'P',
  color: '#9b7fe8'
},
{
  name: 'Rahul Mehta',
  location: 'Delhi, India',
  service: 'Past Life Regression',
  text: 'I was skeptical at first, but the Past Life Regression session completely transformed my perspective. Shruti\'s gentle guidance helped me release deep-seated fears that had been holding me back for years. Truly miraculous.',
  rating: 5,
  initial: 'R',
  color: '#7c5cbf'
},
{
  name: 'Ananya Krishnan',
  location: 'Bangalore, India',
  service: 'Reiki Healing',
  text: 'After struggling with anxiety for years, Shruti\'s Reiki healing sessions brought me back to myself. The transformation has been profound — I sleep better, feel lighter, and approach life with a new sense of joy and clarity.',
  rating: 5,
  initial: 'A',
  color: '#c9a84c'
},
{
  name: 'Vikram Patel',
  location: 'Ahmedabad, India',
  service: 'Clinical Hypnotherapy',
  text: 'As a professional dealing with burnout and limiting beliefs, Shruti\'s Clinical Hypnotherapy was exactly what I needed. Her unique blend of spiritual wisdom and practical tools has made me a better leader and a happier person.',
  rating: 5,
  initial: 'V',
  color: '#b8a0e8'
},
{
  name: 'Meera Nair',
  location: 'Chennai, India',
  service: 'Akashic Record Reading',
  text: 'The Akashic Record Reading with Shruti was unlike anything I\'ve experienced. She has a rare gift for creating sacred space. The session gave me incredible clarity about my life path and soul purpose.',
  rating: 5,
  initial: 'M',
  color: '#e8c96a'
},
{
  name: 'Sunita Agarwal',
  location: 'Jaipur, India',
  service: 'Family Constellation',
  text: 'The Family Constellation session helped me understand and heal generational patterns that had been affecting my relationships for years. Shruti\'s compassionate guidance created a safe space for deep healing.',
  rating: 5,
  initial: 'S',
  color: '#9b7fe8'
}];


const videoTestimonials = [
{
  title: 'Client Transformation Story',
  client: 'Healing Testimonial',
  href: 'https://youtube.com/shorts/FBSrRY1ZhaQ?si=aSfPG07qBHSFq_qk'
},
{
  title: 'Healing Journey Experience',
  client: 'Client Testimonial',
  href: 'https://youtube.com/shorts/DYJi2dHRTE4?si=UgQKS0AGijrqr8uC'
},
{
  title: 'Life-Changing Session',
  client: 'Real Transformation',
  href: 'https://youtube.com/shorts/5EfWlPTR1TA?si=K9r97XfaahpsFNID'
},
{
  title: 'Spiritual Healing Story',
  client: 'Client Experience',
  href: 'https://youtube.com/shorts/3OuZ80WT4xI?si=ThUVDFa_62pHTPqK'
}];


function StarRating({ rating }: {rating: number;}) {
  return (
    <div className="flex gap-1">
      {[...Array(rating)].map((_, i) =>
      <span key={i} className="text-[#c9a84c] text-sm">★</span>
      )}
    </div>);

}

export default function TestimonialsSection() {
  const [titleVisible, setTitleVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setTitleVisible(true);},
      { threshold: 0.1 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section
      id="testimonials"
      className="relative py-24 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0d0818 0%, #130a24 50%, #0d0818 100%)' }}>
      
      <div className="absolute inset-0 lotus-bg opacity-35 pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 glow-orb-lavender"
          style={{ width: '700px', height: '700px', opacity: 0.3 }} />
        
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div
          ref={titleRef}
          className={`text-center mb-16 transition-all duration-1000 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#c9a84c]/60" />
            <span className="text-[#c9a84c] text-xs tracking-[0.4em] uppercase font-medium">Client Transformations</span>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#c9a84c]/60" />
          </div>
          <h2
            className="font-serif text-4xl md:text-5xl font-bold mb-4"
            style={{
              background: 'linear-gradient(135deg, #c4b5fd 0%, #9b7fe8 50%, #c9a84c 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
            
            Transformation Stories
          </h2>
          <div className="section-divider mb-4" />
          <p className="text-[#c4b5fd]/65 max-w-xl mx-auto text-sm leading-relaxed">
            Real stories from souls who have experienced profound healing and transformation through our sacred healing sessions.
          </p>
        </div>

        {/* Video Testimonials */}
        <div className="mb-16">
          <h3
            className="font-serif text-2xl font-semibold text-center mb-8"
            style={{ color: '#e8dff5' }}>
            
            🎥 Video Testimonials
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {videoTestimonials.map((video, index) =>
            <a
              key={index}
              href={video.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02]"
              style={{
                background: 'rgba(22, 12, 40, 0.85)',
                border: '1px solid rgba(201,168,76,0.25)'
              }}>
              
                <div
                className="h-40 flex items-center justify-center relative"
                style={{
                  background: 'linear-gradient(135deg, rgba(124,92,191,0.3) 0%, rgba(201,168,76,0.15) 100%)'
                }}>
                
                  <div
                  className="w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                  style={{
                    background: 'linear-gradient(135deg, #c9a84c 0%, #e8c96a 100%)',
                    boxShadow: '0 4px 20px rgba(201,168,76,0.4)'
                  }}>
                  
                    <span className="text-[#0d0818] text-2xl ml-1">▶</span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <span className="text-[10px] tracking-widest uppercase text-[#c9a84c] border border-[#c9a84c]/40 px-2 py-0.5 rounded-full bg-[#c9a84c]/10">
                      YouTube
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h4 className="font-serif text-sm font-semibold text-[#e8dff5] mb-1">{video.title}</h4>
                  <p className="text-[#c9a84c]/70 text-xs">{video.client}</p>
                </div>
              </a>
            )}
          </div>
          <div className="text-center mt-6">
            <a
              href="https://www.youtube.com/@ShreeVidhaHealingHubb"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#c9a84c] text-xs tracking-widest uppercase hover:text-[#e8c96a] transition-colors font-medium">
              
              View All Video Testimonials →
            </a>
          </div>
        </div>

        {/* Written Reviews */}
        <h3
          className="font-serif text-2xl font-semibold text-center mb-8"
          style={{ color: '#e8dff5' }}>
          
          ⭐ Written Reviews
        </h3>

        {/* Featured Testimonial */}
        <div className="max-w-3xl mx-auto mb-12">
          <div
            className="relative rounded-2xl p-8 md:p-10"
            style={{
              background: 'rgba(22, 12, 40, 0.85)',
              border: '1px solid rgba(201,168,76,0.28)',
              boxShadow: '0 8px 40px rgba(0,0,0,0.35)'
            }}>
            
            <div className="absolute top-6 left-8 font-serif text-6xl leading-none opacity-15 text-[#c9a84c]">
              &ldquo;
            </div>
            <div className="relative">
              <StarRating rating={testimonials[activeIndex].rating} />
              <p className="text-[#e8dff5]/85 text-base leading-relaxed mt-4 mb-6 italic font-light">
                &ldquo;{testimonials[activeIndex].text}&rdquo;
              </p>
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg flex-shrink-0"
                  style={{ background: testimonials[activeIndex].color }}>
                  
                  {testimonials[activeIndex].initial}
                </div>
                <div>
                  <div className="text-[#e8dff5] font-medium text-sm">{testimonials[activeIndex].name}</div>
                  <div className="text-[#c4b5fd]/55 text-xs">{testimonials[activeIndex].location}</div>
                  <div className="text-[#c9a84c]/80 text-xs mt-0.5">{testimonials[activeIndex].service}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-2 mt-6">
            {testimonials.map((_, i) =>
            <button
              key={i}
              onClick={() => setActiveIndex(i)}
              className={`rounded-full transition-all duration-300 ${
              i === activeIndex ? 'w-6 h-2 bg-[#c9a84c]' : 'w-2 h-2 bg-[#c9a84c]/30 hover:bg-[#c9a84c]/60'}`
              }
              aria-label={`Go to testimonial ${i + 1}`} />

            )}
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {testimonials.map((testimonial, index) =>
          <div
            key={testimonial.name}
            className={`testimonial-card cursor-pointer transition-all duration-300 ${
            index === activeIndex ? 'border-[#c9a84c]/50' : ''}`
            }
            onClick={() => setActiveIndex(index)}>
            
              <StarRating rating={testimonial.rating} />
              <p className="text-[#c4b5fd]/65 text-xs leading-relaxed mt-3 mb-4 italic line-clamp-3">
                &ldquo;{testimonial.text}&rdquo;
              </p>
              <div className="flex items-center gap-3">
                <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0"
                style={{ background: testimonial.color }}>
                
                  {testimonial.initial}
                </div>
                <div>
                  <div className="text-[#e8dff5] font-medium text-xs">{testimonial.name}</div>
                  <div className="text-[#c9a84c]/70 text-xs">{testimonial.service}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}